import mockPortfolio from '../portfolio';
import type { HoldingsRequestSubject } from 'venn-state';
import mockFund from '../fund';
import type { HoldingsDataResponse, HoldingsBenchmarkCategory } from 'venn-api';

type CategoryWithoutBenchmark = Omit<HoldingsBenchmarkCategory, 'benchmarkFundUuid' | 'children'> & {
  children: CategoryWithoutBenchmark[];
};

const addBenchmarkFundUuid = (categories: CategoryWithoutBenchmark[]): HoldingsBenchmarkCategory[] =>
  categories.map((cat) => ({
    ...cat,
    benchmarkFundUuid: null,
    children: addBenchmarkFundUuid(cat.children),
  }));

export const assetExposureBlockSetting = {
  customBlockType: 'ASSET_EXPOSURE' as const,
  active: true,
  accessFeatures: [],
  contributionToPercentage: false,
  defaultMetrics: [],
  description: 'description',
  hasFactors: false,
  id: 'asset_exposure_base_id',
  metrics: [],
  supportedGraphicTypes: ['GRID' as const, 'PIE' as const],
  relativeToBenchmark: false,
  title: 'Asset Class',
  updated: 0,
};

export const sectorExposureBlockSetting = {
  customBlockType: 'SECTOR_EXPOSURE' as const,
  active: true,
  accessFeatures: [],
  contributionToPercentage: false,
  defaultMetrics: [],
  description: 'description',
  hasFactors: false,
  id: 'sector_exposure_base_id',
  metrics: [],
  supportedGraphicTypes: ['GRID' as const, 'PIE' as const],
  relativeToBenchmark: false,
  title: 'Sector',
  updated: 0,
};

export const geographyExposureBlockSetting = {
  customBlockType: 'GEOGRAPHY_EXPOSURE' as const,
  active: true,
  accessFeatures: [],
  contributionToPercentage: false,
  defaultMetrics: [],
  description: 'description',
  hasFactors: false,
  id: 'geography_exposure_base_id',
  metrics: [],
  supportedGraphicTypes: ['GRID' as const, 'MAP' as const],
  relativeToBenchmark: false,
  title: 'Geography',
  updated: 0,
};

export const mockHoldingsPortfolio: HoldingsRequestSubject = {
  id: String(mockPortfolio.id),
  name: mockPortfolio.name,
  subjectType: 'PORTFOLIO' as const,
  portfolio: mockPortfolio,
  isBenchmark: false,
  feesMapping: { [String(mockPortfolio.id)]: 0.1 },
  private: false,
  proxySetId: undefined,
};

export const mockHoldingsInvestment: HoldingsRequestSubject = {
  id: mockFund.id,
  name: mockFund.name,
  fund: mockFund,
  subjectType: 'INVESTMENT' as const,
  isBenchmark: false,
  private: false,
  proxySetId: undefined,
};

/** example analysis subject */
export const mockSubject = {
  id: '51542d24-03ba-4f39-bfd6-57a8be5fd986',
  name: 'Pictet-Global Equities Dvrs Alpha I EUR',
  fundSourceType: {
    id: 1,
    name: 'Investment',
  },
  startRange: 1468627199999,
  endRange: 1683158399999,
  returnFrequency: 'DAILY',
  created: 1680648005378,
  updated: 1683618461047,
  createdById: -42,
  updatedById: -42,
  currency: 'EUR',
  domicile: 'Luxembourg',
  netExpenseRatio: 0.12,
  userUploaded: false,
  assetType: 'MUTUAL_FUND',
  remoteType: 'DATA',
  primaryShareClass: false,
  symbol: 'PGDAIAE',
  category: 'NOT_SUPPORTED_CATEGORY',
  categoryAssetType: {
    key: 'MUTUAL_FUND',
    name: 'Mutual Fund',
  },
  categoryGroup: {
    assetType: 'MUTUAL_FUND',
    key: 'MF_',
    name: 'Multistrategy',
    categoryId: '352407ac-194d-4a08-9368-4b55d92631fe',
  },
  active: true,
  live: true,
  owner: {
    id: -42,
    name: 'Venn',
    displayName: 'Venn',
    email: null,
    active: false,
    created: null,
    pwdLastUpdated: null,
    avatarId: null,
    actualUser: false,
    accountId: null,
  },
  updatedBy: {
    id: -42,
    name: 'Venn',
    displayName: 'Venn',
    email: null,
    active: false,
    created: null,
    pwdLastUpdated: null,
    avatarId: null,
    actualUser: false,
    accountId: null,
  },
  frequency: 1,
  unproxiedFrequency: 'DAILY',
  investmentBenchmarks: [],
  favorite: false,
  viewed: false,
  unproxiedNumReturns: 1774,
  needsProxy: false,
  needsInterpolation: false,
  hasMinDataForInterpolation: true,
  outOfDate: false,
  proxyable: true,
  interpolatable: false,
  userEditable: false,
  investmentSource: 'VENN',
};
// all asset categories
export const mockAllHoldingsAssetCategories = [
  {
    id: {
      id: '1265',
    },
    categoryName: 'US Stock',
    children: [],
  },
  {
    id: {
      id: '1266',
    },
    categoryName: 'Non-US Stock',
    children: [],
  },
  {
    id: {
      id: '1267',
    },
    categoryName: 'US Fixed Income',
    children: [],
  },
  {
    id: {
      id: '1268',
    },
    categoryName: 'Non-US Fixed Income',
    children: [],
  },
  {
    id: {
      id: '1269',
    },
    categoryName: 'Preferred',
    children: [],
  },
  {
    id: {
      id: '1270',
    },
    categoryName: 'Convertible',
    children: [],
  },
  {
    id: {
      id: '1271',
    },
    categoryName: 'Cash',
    children: [],
  },
  {
    id: {
      id: '1272',
    },
    categoryName: 'Other',
    children: [],
  },
  {
    id: {
      id: 'UNKNOWN',
    },
    categoryName: 'Unknown',
    children: [],
  },
];

export const mockAllCreditQualityCategories = [
  {
    id: {
      id: '1541',
    },
    categoryName: 'A',
    children: [],
  },
  {
    id: {
      id: '1543',
    },
    categoryName: 'AA',
    children: [],
  },
  {
    id: {
      id: '1545',
    },
    categoryName: 'AAA',
    children: [],
  },
  {
    id: {
      id: '1542',
    },
    categoryName: 'B',
    children: [],
  },
  {
    id: {
      id: '1544',
    },
    categoryName: 'BB',
    children: [],
  },
  {
    id: {
      id: '1546',
    },
    categoryName: 'BBB',
    children: [],
  },
  {
    id: {
      id: '1547',
    },
    categoryName: 'Below B',
    children: [],
  },
  {
    id: {
      id: '1548',
    },
    categoryName: 'Not Rated',
    children: [],
  },
  {
    id: {
      id: 'UNKNOWN',
    },
    categoryName: 'Unknown',
    children: [],
  },
];

export const mockAllMaturityCategories = [
  {
    id: {
      id: '1549',
    },
    categoryName: '0-1 years',
    children: [],
  },
  {
    id: {
      id: '1550',
    },
    categoryName: '1-3 years',
    children: [],
  },
  {
    id: {
      id: '1551',
    },
    categoryName: '10+ years',
    children: [],
  },
  {
    id: {
      id: '1552',
    },
    categoryName: '3-5 years',
    children: [],
  },
  {
    id: {
      id: '1553',
    },
    categoryName: '5-7 years',
    children: [],
  },
  {
    id: {
      id: '1554',
    },
    categoryName: '7-10 years',
    children: [],
  },
  {
    id: {
      id: 'UNKNOWN',
    },
    categoryName: 'Unknown',
    children: [],
  },
];

// example selected asset categories - all ones without US Fixed Income
export const mockSelectedActiveCategories = [
  {
    id: {
      id: '1265',
    },
    categoryName: 'US Stock',
    ordering: 1,
    children: [],
  },
  {
    id: {
      id: '1266',
    },
    categoryName: 'Non-US Stock',
    ordering: 1,
    children: [],
  },
  {
    id: {
      id: '1268',
    },
    categoryName: 'Non-US Fixed Income',
    ordering: 1,
    children: [],
  },
  {
    id: {
      id: '1269',
    },
    categoryName: 'Preferred',
    ordering: 1,
    children: [],
  },
  {
    id: {
      id: '1270',
    },
    categoryName: 'Convertible',
    ordering: 1,
    children: [],
  },
  {
    id: {
      id: '1271',
    },
    categoryName: 'Cash',
    ordering: 1,
    children: [],
  },
  {
    id: {
      id: '1272',
    },
    categoryName: 'Other',
    ordering: 1,
    children: [],
  },
  {
    id: {
      id: 'UNKNOWN',
    },
    categoryName: 'Unknown',
    ordering: 1,
    children: [],
  },
];

// example data for asset categories
export const mockHoldingsAssetCategoriesData = {
  1265: {
    level: 0,
    name: 'US Stock',
    color: '#DE9BC0',
    parentName: '',
    id: '1265',
  },
  1266: {
    level: 0,
    name: 'Non-US Stock',
    color: '#DE9BC0',
    parentName: '',
    id: '1266',
  },
  1267: {
    level: 0,
    name: 'US Fixed Income',
    color: '#DE9BC0',
    parentName: '',
    id: '1267',
  },
  1268: {
    level: 0,
    name: 'Non-US Fixed Income',
    color: '#DE9BC0',
    parentName: '',
    id: '1268',
  },
  1269: {
    level: 0,
    name: 'Preferred',
    color: '#DE9BC0',
    parentName: '',
    id: '1269',
  },
  1270: {
    level: 0,
    name: 'Convertible',
    color: '#DE9BC0',
    parentName: '',
    id: '1270',
  },
  1271: {
    level: 0,
    name: 'Cash',
    color: '#DE9BC0',
    parentName: '',
    id: '1271',
  },
  1272: {
    level: 0,
    name: 'Other',
    color: '#DE9BC0',
    parentName: '',
    id: '1272',
  },
  UNKNOWN: {
    level: 0,
    name: 'Unknown',
    color: '#DE9BC0',
    parentName: '',
    id: 'UNKNOWN',
  },
};

export const mockCreditHoldingsData: HoldingsDataResponse = {
  asOfDate: 1675209599999,
  breakdowns: [
    {
      asOfDate: 1675209599999,
      percentageCovered: 0.19890584945523535,
      holdingsContributions: [
        {
          id: {
            id: '1547',
          },
          allocation: 0.011829328576272134,
        },
        {
          id: {
            id: '1546',
          },
          allocation: 0.043279349765106466,
        },
        {
          id: {
            id: '1545',
          },
          allocation: 0.6226778953112028,
        },
        {
          id: {
            id: '1544',
          },
          allocation: 0.14048570694157955,
        },
        {
          id: {
            id: '1543',
          },
          allocation: 0.05518575669646534,
        },
        {
          id: {
            id: '1542',
          },
          allocation: 0.096455640279287,
        },
        {
          id: {
            id: '1541',
          },
          allocation: 0.02218271243155034,
        },
        {
          id: {
            id: '1548',
          },
          allocation: 0.007903609998536492,
        },
      ],
    },
  ],
  hasNegativeAssets: [false],
  error: undefined,
};

export const mockMaturityHoldingsData: HoldingsDataResponse = {
  asOfDate: 1748044799999,
  breakdowns: [
    {
      asOfDate: 1748044799999,
      percentageCovered: 0.19890584945523535,
      holdingsContributions: [
        {
          id: {
            id: '1554',
          },
          allocation: 0.0852,
        },
        {
          id: {
            id: '1553',
          },
          allocation: 0.2782,
        },
        {
          id: {
            id: '1552',
          },
          allocation: 0.3818,
        },
        {
          id: {
            id: '1551',
          },
          allocation: 0.0217,
        },
        {
          id: {
            id: '1550',
          },
          allocation: 0.2108,
        },
        {
          id: {
            id: '1549',
          },
          allocation: 0.0223,
        },
      ],
    },
  ],
  hasNegativeAssets: [false],
  error: undefined,
};

// example holdings data for asset class
export const mockHoldingsData: HoldingsDataResponse = {
  asOfDate: 1675209599999,
  breakdowns: [
    {
      asOfDate: 1675209599999,
      percentageCovered: 1,
      holdingsContributions: [
        {
          id: {
            id: '1268',
          },
          allocation: -0.053099999999999994,
        },
        {
          id: {
            id: '1267',
          },
          allocation: 0.1231,
        },
        {
          id: {
            id: '1266',
          },
          allocation: 0.3514,
        },
        {
          id: {
            id: '1265',
          },
          allocation: -0.026099999999999998,
        },
        {
          id: {
            id: '1272',
          },
          allocation: -0.0374,
        },
        {
          id: {
            id: '1271',
          },
          allocation: 0.6389,
        },
        {
          id: {
            id: '1270',
          },
          allocation: 0.0032,
        },
      ],
    },
  ],
  hasNegativeAssets: [false],
  error: undefined,
};

export const mockSelectedSectorCategories = [
  {
    id: {
      id: '796',
    },
    categoryName: 'Equity',
    children: [],
  },
  {
    id: {
      id: '797',
    },
    categoryName: 'Basic Materials',
    children: [],
  },
  {
    id: {
      id: '798',
    },
    categoryName: 'Agriculture Group',
    children: [],
  },
  {
    id: {
      id: 'UNKNOWN',
    },
    categoryName: 'Unknown',
    children: [],
  },
];

export const mockHoldingsSectorData: HoldingsDataResponse = {
  asOfDate: 1675209599999,
  breakdowns: [
    {
      percentageCovered: 1,
      asOfDate: 1675209599999,
      holdingsContributions: [
        {
          id: {
            id: '796',
          },
          allocation: -0.053099999999999994,
        },
        {
          id: {
            id: '797',
          },
          allocation: 0.1231,
        },
        {
          id: {
            id: '798',
          },
          allocation: 0.3514,
        },
        {
          id: {
            id: '799',
          },
          allocation: -0.026099999999999998,
        },
        {
          id: {
            id: '800',
          },
          allocation: -0.0374,
        },
        {
          id: {
            id: '801',
          },
          allocation: 0.6389,
        },
        {
          id: {
            id: 'UNKNOWN',
          },
          allocation: 0.0032,
        },
      ],
    },
  ],
  hasNegativeAssets: [false],
  error: undefined,
};

export const mockHoldingsSectorCategories = {
  796: { color: '#BBBDBF', level: 1, parentName: 'EQUITY', name: 'Cyclical', parentId: 'EQUITY', id: '796' },
  797: { color: '#33C08F', level: 2, parentName: 'Cyclical', name: 'Basic Materials', parentId: '796', id: '797' },
  798: {
    color: '#BBBDBF',
    level: 3,
    parentName: 'Basic Materials',
    name: 'Agriculture Group',
    parentId: '797',
    id: '798',
  },
  799: {
    color: '#BBBDBF',
    level: 4,
    parentName: 'Agricultural Group',
    name: 'Agricultural Inputs',
    parentId: '798',
    id: '799',
  },
  800: {
    color: '#BBBDBF',
    level: 3,
    parentName: 'Basic Materials',
    name: 'Building Materials Group',
    parentId: '797',
    id: '800',
  },
  801: {
    color: '#BBBDBF',
    level: 4,
    parentName: 'Building Materials Group',
    name: 'Building Materials',
    parentId: '800',
    id: '801',
  },
  802: {
    color: '#BBBDBF',
    level: 3,
    parentName: 'Basic Materials',
    name: 'Chemicals Group',
    parentId: '797',
    id: '802',
  },
  803: { color: '#BBBDBF', level: 4, parentName: 'Chemicals Group', name: 'Chemicals', parentId: '802', id: '803' },
  804: {
    color: '#BBBDBF',
    level: 4,
    parentName: 'Chemicals Group',
    name: 'Specialty Chemicals',
    parentId: '802',
    id: '804',
  },
  805: {
    color: '#BBBDBF',
    level: 3,
    parentName: 'Basic Materials',
    name: 'Forest Products Group',
    parentId: '797',
    id: '805',
  },
  806: {
    color: '#BBBDBF',
    level: 4,
    parentName: 'Forest Products Group',
    name: 'Lumber & Wood Production',
    parentId: '805',
    id: '806',
  },
  807: {
    color: '#BBBDBF',
    level: 4,
    parentName: 'Forest Products Group',
    name: 'Paper & Paper Products',
    parentId: '805',
    id: '807',
  },
  808: {
    color: '#BBBDBF',
    level: 3,
    parentName: 'Basic Materials',
    name: 'Metals & Mining Group',
    parentId: '797',
    id: '808',
  },
  809: {
    color: '#BBBDBF',
    level: 4,
    parentName: 'Metals & Mining Group',
    name: 'Aluminum',
    parentId: '808',
    id: '809',
  },
  810: { color: '#BBBDBF', level: 4, parentName: 'Metals & Mining Group', name: 'Copper', parentId: '808', id: '810' },
  811: {
    color: '#BBBDBF',
    level: 4,
    parentName: 'Metals & Mining Group',
    name: 'Other Industrial Metals & Mining',
    parentId: '808',
    id: '811',
  },
  812: { color: '#BBBDBF', level: 4, parentName: 'Metals & Mining Group', name: 'Gold', parentId: '808', id: '812' },
  EQUITY: { color: '#BBBDBF', level: 0, parentName: '', name: 'Equity', id: 'EQUITY' },
  unknown: { color: '#BBBDBF', level: 0, parentName: '', name: 'Not Classified', id: 'unknown' },
  UNKNOWN: { color: '#BBBDBF', level: 1, parentName: 'unknown', name: 'Unknown', id: 'UNKNOWN', parentId: 'unknown' },
};

const mockHoldingsSectorTreeRaw: CategoryWithoutBenchmark[] = [
  {
    id: { id: 'EQUITY' },
    categoryName: 'Equity',
    ordering: 1,
    children: [
      {
        id: { id: '796' },
        categoryName: 'Cyclical',
        ordering: 1,
        children: [
          {
            id: { id: '797' },
            categoryName: 'Basic Materials',
            ordering: 1,
            children: [
              {
                id: { id: '798' },
                categoryName: 'Agriculture Group',
                ordering: 1,
                children: [{ id: { id: '799' }, categoryName: 'Agricultural Inputs', ordering: 1, children: [] }],
              },
              {
                id: { id: '800' },
                categoryName: 'Building Materials Group',
                ordering: 1,
                children: [{ id: { id: '801' }, categoryName: 'Building Materials', ordering: 1, children: [] }],
              },
              {
                id: { id: '802' },
                categoryName: 'Chemicals Group',
                ordering: 1,
                children: [
                  { id: { id: '803' }, categoryName: 'Chemicals', ordering: 1, children: [] },
                  { id: { id: '804' }, categoryName: 'Specialty Chemicals', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '805' },
                categoryName: 'Forest Products Group',
                ordering: 1,
                children: [
                  { id: { id: '806' }, categoryName: 'Lumber & Wood Production', ordering: 1, children: [] },
                  { id: { id: '807' }, categoryName: 'Paper & Paper Products', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '808' },
                categoryName: 'Metals & Mining Group',
                ordering: 1,
                children: [
                  { id: { id: '809' }, categoryName: 'Aluminum', ordering: 1, children: [] },
                  { id: { id: '810' }, categoryName: 'Copper', ordering: 1, children: [] },
                  { id: { id: '811' }, categoryName: 'Other Industrial Metals & Mining', ordering: 1, children: [] },
                  { id: { id: '812' }, categoryName: 'Gold', ordering: 1, children: [] },
                  { id: { id: '813' }, categoryName: 'Silver', ordering: 1, children: [] },
                  { id: { id: '814' }, categoryName: 'Other Precious Metals & Mining', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '815' },
                categoryName: 'Steel Group',
                ordering: 1,
                children: [
                  { id: { id: '816' }, categoryName: 'Coking Coal', ordering: 1, children: [] },
                  { id: { id: '817' }, categoryName: 'Steel', ordering: 1, children: [] },
                ],
              },
            ],
          },
          {
            id: { id: '818' },
            categoryName: 'Consumer Cyclical',
            ordering: 1,
            children: [
              {
                id: { id: '819' },
                categoryName: 'Vehicles & Parts Group',
                ordering: 1,
                children: [
                  { id: { id: '820' }, categoryName: 'Auto & Truck Dealerships', ordering: 1, children: [] },
                  { id: { id: '821' }, categoryName: 'Auto Manufacturers', ordering: 1, children: [] },
                  { id: { id: '822' }, categoryName: 'Auto Parts', ordering: 1, children: [] },
                  { id: { id: '823' }, categoryName: 'Recreational Vehicles', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '824' },
                categoryName: 'Furnishings, Fixtures & Appliances Group',
                ordering: 1,
                children: [
                  {
                    id: { id: '825' },
                    categoryName: 'Furnishings, Fixtures & Appliances',
                    ordering: 1,
                    children: [],
                  },
                ],
              },
              {
                id: { id: '826' },
                categoryName: 'Homebuilding & Construction Group',
                ordering: 1,
                children: [
                  {
                    id: { id: '827' },
                    categoryName: 'Residential Construction',
                    ordering: 1,
                    children: [],
                  },
                ],
              },
              {
                id: { id: '828' },
                categoryName: 'Manufacturing—Apparel & Accessories Group',
                ordering: 1,
                children: [
                  { id: { id: '829' }, categoryName: 'Textile Manufacturing', ordering: 1, children: [] },
                  { id: { id: '830' }, categoryName: 'Apparel Manufacturing', ordering: 1, children: [] },
                  { id: { id: '831' }, categoryName: 'Footwear & Accessories', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '832' },
                categoryName: 'Packaging & Containers Group',
                ordering: 1,
                children: [{ id: { id: '833' }, categoryName: 'Packaging & Containers', ordering: 1, children: [] }],
              },
              {
                id: { id: '834' },
                categoryName: 'Personal Services Group',
                ordering: 1,
                children: [{ id: { id: '835' }, categoryName: 'Personal Services', ordering: 1, children: [] }],
              },
              {
                id: { id: '836' },
                categoryName: 'Restaurants Group',
                ordering: 1,
                children: [{ id: { id: '837' }, categoryName: 'Restaurants', ordering: 1, children: [] }],
              },
              {
                id: { id: '838' },
                categoryName: 'Retail—Cyclical Group',
                ordering: 1,
                children: [
                  { id: { id: '839' }, categoryName: 'Apparel Retail', ordering: 1, children: [] },
                  { id: { id: '840' }, categoryName: 'Department Stores', ordering: 1, children: [] },
                  { id: { id: '841' }, categoryName: 'Home Improvement Retail', ordering: 1, children: [] },
                  { id: { id: '842' }, categoryName: 'Luxury Goods', ordering: 1, children: [] },
                  { id: { id: '843' }, categoryName: 'Internet Retail', ordering: 1, children: [] },
                  { id: { id: '844' }, categoryName: 'Specialty Retail', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '845' },
                categoryName: 'Travel & Leisure Group',
                ordering: 1,
                children: [
                  { id: { id: '846' }, categoryName: 'Gambling', ordering: 1, children: [] },
                  { id: { id: '847' }, categoryName: 'Leisure', ordering: 1, children: [] },
                  { id: { id: '848' }, categoryName: 'Lodging', ordering: 1, children: [] },
                  { id: { id: '849' }, categoryName: 'Resorts & Casinos', ordering: 1, children: [] },
                  { id: { id: '850' }, categoryName: 'Travel Services', ordering: 1, children: [] },
                ],
              },
            ],
          },
          {
            id: { id: '851' },
            categoryName: 'Financial Services',
            ordering: 1,
            children: [
              {
                id: { id: '852' },
                categoryName: 'Asset Management Group',
                ordering: 1,
                children: [{ id: { id: '853' }, categoryName: 'Asset Management', ordering: 1, children: [] }],
              },
              {
                id: { id: '854' },
                categoryName: 'Banks Group',
                ordering: 1,
                children: [
                  { id: { id: '855' }, categoryName: 'Banks—Diversified', ordering: 1, children: [] },
                  { id: { id: '856' }, categoryName: 'Banks—Regional', ordering: 1, children: [] },
                  { id: { id: '857' }, categoryName: 'Mortgage Finance', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '858' },
                categoryName: 'Capital Markets Group',
                ordering: 1,
                children: [
                  { id: { id: '859' }, categoryName: 'Capital Markets', ordering: 1, children: [] },
                  { id: { id: '860' }, categoryName: 'Financial Data & Stock Exchanges', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '861' },
                categoryName: 'Insurance Group',
                ordering: 1,
                children: [
                  { id: { id: '862' }, categoryName: 'Insurance—Life', ordering: 1, children: [] },
                  { id: { id: '863' }, categoryName: 'Insurance—Property & Casualty', ordering: 1, children: [] },
                  { id: { id: '864' }, categoryName: 'Insurance—Reinsurance', ordering: 1, children: [] },
                  { id: { id: '865' }, categoryName: 'Insurance—Specialty', ordering: 1, children: [] },
                  { id: { id: '866' }, categoryName: 'Insurance Brokers', ordering: 1, children: [] },
                  { id: { id: '867' }, categoryName: 'Insurance—Diversified', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '868' },
                categoryName: 'Diversified Financial Services Group',
                ordering: 1,
                children: [
                  { id: { id: '869' }, categoryName: 'Shell Companies', ordering: 1, children: [] },
                  { id: { id: '870' }, categoryName: 'Financial Conglomerates', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '871' },
                categoryName: 'Credit Services Group',
                ordering: 1,
                children: [{ id: { id: '872' }, categoryName: 'Credit Services', ordering: 1, children: [] }],
              },
            ],
          },
          {
            id: { id: '873' },
            categoryName: 'Real Estate',
            ordering: 1,
            children: [
              {
                id: { id: '874' },
                categoryName: 'Real Estate Group',
                ordering: 1,
                children: [
                  { id: { id: '875' }, categoryName: 'Real Estate—Development', ordering: 1, children: [] },
                  { id: { id: '876' }, categoryName: 'Real Estate Services', ordering: 1, children: [] },
                  { id: { id: '877' }, categoryName: 'Real Estate—Diversified', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '878' },
                categoryName: 'REITs Group',
                ordering: 1,
                children: [
                  { id: { id: '879' }, categoryName: 'REIT—Healthcare Facilities', ordering: 1, children: [] },
                  { id: { id: '880' }, categoryName: 'REIT—Hotel & Motel', ordering: 1, children: [] },
                  { id: { id: '881' }, categoryName: 'REIT—Industrial', ordering: 1, children: [] },
                  { id: { id: '882' }, categoryName: 'REIT—Office', ordering: 1, children: [] },
                  { id: { id: '883' }, categoryName: 'REIT—Residential', ordering: 1, children: [] },
                  { id: { id: '884' }, categoryName: 'REIT—Retail', ordering: 1, children: [] },
                  { id: { id: '885' }, categoryName: 'REIT—Mortgage', ordering: 1, children: [] },
                  { id: { id: '886' }, categoryName: 'REIT—Specialty', ordering: 1, children: [] },
                  { id: { id: '887' }, categoryName: 'REIT—Diversified', ordering: 1, children: [] },
                ],
              },
            ],
          },
        ],
      },
      {
        id: { id: '888' },
        categoryName: 'Defensive',
        ordering: 1,
        children: [
          {
            id: { id: '889' },
            categoryName: 'Consumer Defensive',
            ordering: 1,
            children: [
              {
                id: { id: '890' },
                categoryName: 'Beverages—Alcoholic Group',
                ordering: 1,
                children: [
                  { id: { id: '891' }, categoryName: 'Beverages—Brewers', ordering: 1, children: [] },
                  {
                    id: { id: '892' },
                    categoryName: 'Beverages—Wineries & Distilleries',
                    ordering: 1,
                    children: [],
                  },
                ],
              },
              {
                id: { id: '893' },
                categoryName: 'Beverages—Non-Alcoholic Group',
                ordering: 1,
                children: [
                  {
                    id: { id: '894' },
                    categoryName: 'Beverages—Non-Alcoholic',
                    ordering: 1,
                    children: [],
                  },
                ],
              },
              {
                id: { id: '895' },
                categoryName: 'Consumer Packaged Goods Group',
                ordering: 1,
                children: [
                  { id: { id: '896' }, categoryName: 'Confectioners', ordering: 1, children: [] },
                  { id: { id: '897' }, categoryName: 'Farm Products', ordering: 1, children: [] },
                  { id: { id: '898' }, categoryName: 'Household & Personal Products', ordering: 1, children: [] },
                  { id: { id: '899' }, categoryName: 'Packaged Foods', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '900' },
                categoryName: 'Education Group',
                ordering: 1,
                children: [
                  {
                    id: { id: '901' },
                    categoryName: 'Education & Training Services',
                    ordering: 1,
                    children: [],
                  },
                ],
              },
              {
                id: { id: '902' },
                categoryName: 'Retail—Defensive Group',
                ordering: 1,
                children: [
                  { id: { id: '903' }, categoryName: 'Discount Stores', ordering: 1, children: [] },
                  { id: { id: '904' }, categoryName: 'Food Distribution', ordering: 1, children: [] },
                  { id: { id: '905' }, categoryName: 'Grocery Stores', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '906' },
                categoryName: 'Tobacco Products Group',
                ordering: 1,
                children: [{ id: { id: '907' }, categoryName: 'Tobacco', ordering: 1, children: [] }],
              },
            ],
          },
          {
            id: { id: '908' },
            categoryName: 'Healthcare',
            ordering: 1,
            children: [
              {
                id: { id: '909' },
                categoryName: 'Biotechnology Group',
                ordering: 1,
                children: [{ id: { id: '910' }, categoryName: 'Biotechnology', ordering: 1, children: [] }],
              },
              {
                id: { id: '911' },
                categoryName: 'Drug Manufacturers Group',
                ordering: 1,
                children: [
                  { id: { id: '912' }, categoryName: 'Drug Manufacturers—General', ordering: 1, children: [] },
                  {
                    id: { id: '913' },
                    categoryName: 'Drug Manufacturers—Specialty & Generic',
                    ordering: 1,
                    children: [],
                  },
                ],
              },
              {
                id: { id: '914' },
                categoryName: 'Healthcare Plans Group',
                ordering: 1,
                children: [{ id: { id: '915' }, categoryName: 'Healthcare Plans', ordering: 1, children: [] }],
              },
              {
                id: { id: '916' },
                categoryName: 'Healthcare Providers & Services Group',
                ordering: 1,
                children: [
                  { id: { id: '917' }, categoryName: 'Medical Care Facilities', ordering: 1, children: [] },
                  { id: { id: '918' }, categoryName: 'Pharmaceutical Retailers', ordering: 1, children: [] },
                  { id: { id: '919' }, categoryName: 'Health Information Services', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '920' },
                categoryName: 'Medical Devices & Instruments Group',
                ordering: 1,
                children: [
                  { id: { id: '921' }, categoryName: 'Medical Devices', ordering: 1, children: [] },
                  { id: { id: '922' }, categoryName: 'Medical Instruments & Supplies', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '923' },
                categoryName: 'Medical Diagnostics & Research Group',
                ordering: 1,
                children: [{ id: { id: '924' }, categoryName: 'Diagnostics & Research', ordering: 1, children: [] }],
              },
              {
                id: { id: '925' },
                categoryName: 'Medical Distribution Group',
                ordering: 1,
                children: [{ id: { id: '926' }, categoryName: 'Medical Distribution', ordering: 1, children: [] }],
              },
            ],
          },
          {
            id: { id: '927' },
            categoryName: 'Utilities',
            ordering: 1,
            children: [
              {
                id: { id: '928' },
                categoryName: 'Utilities—Independent Power Producers Group',
                ordering: 1,
                children: [
                  {
                    id: { id: '929' },
                    categoryName: 'Utilities—Independent Power Producers',
                    ordering: 1,
                    children: [],
                  },
                  { id: { id: '930' }, categoryName: 'Utilities—Renewable', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '931' },
                categoryName: 'Utilities—Regulated Group',
                ordering: 1,
                children: [
                  { id: { id: '932' }, categoryName: 'Utilities—Regulated Water', ordering: 1, children: [] },
                  { id: { id: '933' }, categoryName: 'Utilities—Regulated Electric', ordering: 1, children: [] },
                  { id: { id: '934' }, categoryName: 'Utilities—Regulated Gas', ordering: 1, children: [] },
                  { id: { id: '935' }, categoryName: 'Utilities—Diversified', ordering: 1, children: [] },
                ],
              },
            ],
          },
        ],
      },
      {
        id: { id: '936' },
        categoryName: 'Sensitive',
        ordering: 1,
        children: [
          {
            id: { id: '937' },
            categoryName: 'Communication Services',
            ordering: 1,
            children: [
              {
                id: { id: '938' },
                categoryName: 'Telecommunication Services Group',
                ordering: 1,
                children: [{ id: { id: '939' }, categoryName: 'Telecom Services', ordering: 1, children: [] }],
              },
              {
                id: { id: '940' },
                categoryName: 'Media—Diversified Group',
                ordering: 1,
                children: [
                  { id: { id: '941' }, categoryName: 'Advertising Agencies', ordering: 1, children: [] },
                  { id: { id: '942' }, categoryName: 'Publishing', ordering: 1, children: [] },
                  { id: { id: '943' }, categoryName: 'Broadcasting', ordering: 1, children: [] },
                  { id: { id: '944' }, categoryName: 'Entertainment', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '945' },
                categoryName: 'Interactive Media Group',
                ordering: 1,
                children: [
                  { id: { id: '946' }, categoryName: 'Internet Content & Information', ordering: 1, children: [] },
                  { id: { id: '947' }, categoryName: 'Electronic Gaming & Multimedia', ordering: 1, children: [] },
                ],
              },
            ],
          },
          {
            id: { id: '948' },
            categoryName: 'Energy',
            ordering: 1,
            children: [
              {
                id: { id: '949' },
                categoryName: 'Oil & Gas Group',
                ordering: 1,
                children: [
                  { id: { id: '950' }, categoryName: 'Oil & Gas Drilling', ordering: 1, children: [] },
                  { id: { id: '951' }, categoryName: 'Oil & Gas E&P', ordering: 1, children: [] },
                  { id: { id: '952' }, categoryName: 'Oil & Gas Integrated', ordering: 1, children: [] },
                  { id: { id: '953' }, categoryName: 'Oil & Gas Midstream', ordering: 1, children: [] },
                  { id: { id: '954' }, categoryName: 'Oil & Gas Refining & Marketing', ordering: 1, children: [] },
                  { id: { id: '955' }, categoryName: 'Oil & Gas Equipment & Services', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '956' },
                categoryName: 'Other Energy Sources Group',
                ordering: 1,
                children: [
                  { id: { id: '957' }, categoryName: 'Thermal Coal', ordering: 1, children: [] },
                  { id: { id: '958' }, categoryName: 'Uranium', ordering: 1, children: [] },
                ],
              },
            ],
          },
          {
            id: { id: '959' },
            categoryName: 'Industrials',
            ordering: 1,
            children: [
              {
                id: { id: '960' },
                categoryName: 'Aerospace & Defense Group',
                ordering: 1,
                children: [{ id: { id: '961' }, categoryName: 'Aerospace & Defense', ordering: 1, children: [] }],
              },
              {
                id: { id: '962' },
                categoryName: 'Business Services Group',
                ordering: 1,
                children: [
                  { id: { id: '963' }, categoryName: 'Specialty Business Services', ordering: 1, children: [] },
                  { id: { id: '964' }, categoryName: 'Consulting Services', ordering: 1, children: [] },
                  { id: { id: '965' }, categoryName: 'Rental & Leasing Services', ordering: 1, children: [] },
                  { id: { id: '966' }, categoryName: 'Security & Protection Services', ordering: 1, children: [] },
                  { id: { id: '967' }, categoryName: 'Staffing & Employment Services', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '968' },
                categoryName: 'Conglomerates Group',
                ordering: 1,
                children: [{ id: { id: '969' }, categoryName: 'Conglomerates', ordering: 1, children: [] }],
              },
              {
                id: { id: '970' },
                categoryName: 'Construction Group',
                ordering: 1,
                children: [
                  { id: { id: '971' }, categoryName: 'Engineering & Construction', ordering: 1, children: [] },
                  { id: { id: '972' }, categoryName: 'Infrastructure Operations', ordering: 1, children: [] },
                  { id: { id: '973' }, categoryName: 'Building Products & Equipment', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '974' },
                categoryName: 'Farm & Heavy Construction Machinery Group',
                ordering: 1,
                children: [
                  {
                    id: { id: '975' },
                    categoryName: 'Farm & Heavy Construction Machinery',
                    ordering: 1,
                    children: [],
                  },
                ],
              },
              {
                id: { id: '976' },
                categoryName: 'Industrial Distribution Group',
                ordering: 1,
                children: [
                  {
                    id: { id: '977' },
                    categoryName: 'Industrial Distribution',
                    ordering: 1,
                    children: [],
                  },
                ],
              },
              {
                id: { id: '978' },
                categoryName: 'Industrial Products Group',
                ordering: 1,
                children: [
                  { id: { id: '979' }, categoryName: 'Business Equipment & Supplies', ordering: 1, children: [] },
                  { id: { id: '980' }, categoryName: 'Specialty Industrial Machinery', ordering: 1, children: [] },
                  { id: { id: '981' }, categoryName: 'Metal Fabrication', ordering: 1, children: [] },
                  { id: { id: '982' }, categoryName: 'Pollution & Treatment Controls', ordering: 1, children: [] },
                  { id: { id: '983' }, categoryName: 'Tools & Accessories', ordering: 1, children: [] },
                  { id: { id: '984' }, categoryName: 'Electrical Equipment & Parts', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '985' },
                categoryName: 'Transportation Group',
                ordering: 1,
                children: [
                  { id: { id: '986' }, categoryName: 'Airports & Air Services', ordering: 1, children: [] },
                  { id: { id: '987' }, categoryName: 'Airlines', ordering: 1, children: [] },
                  { id: { id: '988' }, categoryName: 'Railroads', ordering: 1, children: [] },
                  { id: { id: '989' }, categoryName: 'Marine Shipping', ordering: 1, children: [] },
                  { id: { id: '990' }, categoryName: 'Trucking', ordering: 1, children: [] },
                  { id: { id: '991' }, categoryName: 'Integrated Freight & Logistics', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '992' },
                categoryName: 'Waste Management Group',
                ordering: 1,
                children: [{ id: { id: '993' }, categoryName: 'Waste Management', ordering: 1, children: [] }],
              },
            ],
          },
          {
            id: { id: '994' },
            categoryName: 'Technology',
            ordering: 1,
            children: [
              {
                id: { id: '995' },
                categoryName: 'Software Group',
                ordering: 1,
                children: [
                  { id: { id: '996' }, categoryName: 'Information Technology Services', ordering: 1, children: [] },
                  { id: { id: '997' }, categoryName: 'Software—Application', ordering: 1, children: [] },
                  { id: { id: '998' }, categoryName: 'Software—Infrastructure', ordering: 1, children: [] },
                ],
              },
              {
                id: { id: '999' },
                categoryName: 'Hardware Group',
                ordering: 1,
                children: [
                  { id: { id: '1000' }, categoryName: 'Communication Equipment', ordering: 1, children: [] },
                  { id: { id: '1001' }, categoryName: 'Computer Hardware', ordering: 1, children: [] },
                  { id: { id: '1002' }, categoryName: 'Consumer Electronics', ordering: 1, children: [] },
                  { id: { id: '1003' }, categoryName: 'Electronic Components', ordering: 1, children: [] },
                  {
                    id: { id: '1004' },
                    categoryName: 'Electronics & Computer Distribution',
                    ordering: 1,
                    children: [],
                  },
                  {
                    id: { id: '1005' },
                    categoryName: 'Scientific & Technical Instruments',
                    ordering: 1,
                    children: [],
                  },
                ],
              },
              {
                id: { id: '1006' },
                categoryName: 'Semiconductors Group',
                ordering: 1,
                children: [
                  {
                    id: { id: '1007' },
                    categoryName: 'Semiconductor Equipment & Materials',
                    ordering: 1,
                    children: [],
                  },
                  { id: { id: '1008' }, categoryName: 'Semiconductors', ordering: 1, children: [] },
                  { id: { id: '1009' }, categoryName: 'Solar', ordering: 1, children: [] },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
  {
    id: { id: 'BOND' },
    categoryName: 'Fixed Income',
    ordering: 1,
    children: [
      { id: { id: '1313' }, categoryName: 'Corporate Bond', ordering: 1, children: [] },
      {
        id: { id: '1315' },
        categoryName: 'Securitized',
        ordering: 1,
        children: [
          { id: { id: '1316' }, categoryName: 'Agency Mortgage Backed', ordering: 1, children: [] },
          { id: { id: '1317' }, categoryName: 'Non Agency Residential Mortgage Backed', ordering: 1, children: [] },
          { id: { id: '1318' }, categoryName: 'Commercial Mortgage Backed', ordering: 1, children: [] },
          { id: { id: '1319' }, categoryName: 'Covered Bond', ordering: 1, children: [] },
          { id: { id: '1320' }, categoryName: 'Asset Backed', ordering: 1, children: [] },
        ],
      },
      { id: { id: '1321' }, categoryName: 'Cash And Equivalents', ordering: 1, children: [] },
      {
        id: { id: '1322' },
        categoryName: 'Derivative',
        ordering: 1,
        children: [
          { id: { id: '1323' }, categoryName: 'Swap', ordering: 1, children: [] },
          { id: { id: '1324' }, categoryName: 'Future Forward', ordering: 1, children: [] },
          { id: { id: '1325' }, categoryName: 'Option Warrant', ordering: 1, children: [] },
        ],
      },
      { id: { id: '1305' }, categoryName: 'Government', ordering: 1, children: [] },
      { id: { id: '1306' }, categoryName: 'Government Related', ordering: 1, children: [] },
      {
        id: { id: '1307' },
        categoryName: 'Municipal',
        ordering: 1,
        children: [
          { id: { id: '1308' }, categoryName: 'Municipal Taxable', ordering: 1, children: [] },
          { id: { id: '1309' }, categoryName: 'US Municipal Tax Advantaged', ordering: 1, children: [] },
        ],
      },
      {
        id: { id: '1310' },
        categoryName: 'Corporate',
        ordering: 1,
        children: [
          { id: { id: '1312' }, categoryName: 'Convertible', ordering: 1, children: [] },
          { id: { id: '1314' }, categoryName: 'Preferred', ordering: 1, children: [] },
          { id: { id: '1311' }, categoryName: 'Bank Loan', ordering: 1, children: [] },
        ],
      },
    ],
  },
  {
    id: { id: 'unknown' },
    categoryName: 'Not Classified',
    ordering: 1,
    children: [{ id: { id: 'UNKNOWN' }, categoryName: 'Unknown', ordering: 1, children: [] }],
  },
];

export const mockHoldingsSectorTree = addBenchmarkFundUuid(mockHoldingsSectorTreeRaw);
